import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axiosInstance from "./../../apis/config";
import Images from "../../components/productDetails/images";
import Details from "../../components/productDetails/details";
import FullDescription from "../../components/productDetails/fullDescription";
import RelatedProducts from "../../components/productDetails/relatedProducts";
import Spinner from "../../components/common/spinner";
import "../../components/common/homeProductDetails.css";

const ProductDetails = () => {
  const [data, setData] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState(null);
  const { id } = useParams();
  const location = useLocation();

  useEffect(() => {
    axiosInstance
      .get(`/products/${id}`)
      .then(async (res) => {
        setData(res.data);
        await axiosInstance
          .get("/products", {
            params: {
              page: 1,
              price: 0,
              category: res.data.category,
            },
          })
          .then((productsRes) => setRelatedProducts(productsRes.data.data))
          .catch((error) => console.log(error));
      })
      .catch((error) => console.log(error));
  }, [id]);

  // Generate structured data for SEO
  const productStructuredData = data
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: data.name,
        description: data.description.slice(0, 160),
        image: data.documents.map((doc) => doc.url),
        sku: data._id,
        brand: {
          "@type": "Brand",
          name: data.brand || "Nakuru Security Solutions",
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "KES",
          price: data.price,
          availability: data.colors?.some((c) => c.stock > 0)
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          itemCondition: "https://schema.org/NewCondition",
          url: window.location.href,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: data.ratings,
          reviewCount: data.reviews?.length || 0,
        },
      }
    : null;

  // Get current URL for canonical tag
  const currentUrl = window.location.origin + location.pathname;
  const featuredImage = data?.documents?.[0]?.url || "";
  const categoryName = data?.category?.name || "Security Products";

  return data ? (
    <div className="container" itemScope itemType="https://schema.org/Product">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{`${data.name} - ${categoryName} in Nakuru, Kenya`}</title>
        <meta
          name="title"
          content={`${data.name} - ${categoryName} in Nakuru, Kenya`}
        />
        <meta
          name="description"
          content={data.description.slice(0, 160) + "..."}
        />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta
          property="og:title"
          content={`${data.name} - ${categoryName} in Nakuru, Kenya`}
        />
        <meta
          property="og:description"
          content={data.description.slice(0, 160) + "..."}
        />
        <meta property="og:image" content={featuredImage} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="Nakuru Security Solutions" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${data.name} - ${categoryName} in Nakuru, Kenya`}
        />
        <meta
          name="twitter:description"
          content={data.description.slice(0, 160) + "..."}
        />
        <meta name="twitter:image" content={featuredImage} />
        <meta name="twitter:site" content="@NakuruSecurity" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productStructuredData)}
        </script>

        {/* Additional SEO Meta */}
        <meta
          name="keywords"
          content={`${data.name}, ${categoryName}, security products, Nakuru, Kenya, ${data.brand}`}
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Nakuru Security Solutions" />
        <meta itemProp="name" content={data.name} />
        <meta itemProp="sku" content={data._id} />
        <meta itemProp="category" content={categoryName} />
      </Helmet>

      <div className="product-featured">
        <div className="showcase-wrapper has-scrollbar">
          <div className="showcase-container">
            <div id="prodcut-details" className="pb-5">
              <div className="container-fluid border-bottom py-5">
                <h1 className="sr-only">
                  {data.name} - Security Product in Nakuru, Kenya
                </h1>

                <div className="row m-0 mb-5">
                  <div className="col-lg-6 col-md-6 mb-md-0 mb-4">
                    <Images
                      imgs={data?.documents}
                      altText={`${data.name} security product installation in Nakuru, Kenya`}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6">
                    <Details product={data} location="Nakuru, Kenya" />
                  </div>
                </div>
                <FullDescription
                  description={data?.description}
                  name={data?.name}
                  data={data}
                  location="Nakuru"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <RelatedProducts
        data={relatedProducts}
        location="Nakuru"
        category={data.category?.name}
      />
    </div>
  ) : (
    <div className="py-5 my-5">
      <Spinner message={"Loading security product details..."} />
      <Helmet>
        <title>
          Loading Security Product Details - Nakuru Security Solutions
        </title>
        <meta
          name="description"
          content="View detailed specifications and pricing for security products in Nakuru, Kenya"
        />
      </Helmet>
    </div>
  );
};

export default ProductDetails;
